import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NavigationStart, Router, RouterEvent } from '@angular/router';
import { ComponentRendering, Item } from '@sitecore-jss/sitecore-jss/layout';
import { Subject, Subscription, combineLatest, filter, interval, switchMap, takeUntil, takeWhile } from 'rxjs';
import { LoginRequest, LoginService } from '../../../../login/services/login.service';
import { AB_SENTOO_TRANSFER } from '../../../../shared/constants';
import { FormFields } from '../../../../shared/services/form-fields-generator.service';
import { GetItemListFieldsService } from '../../../../shared/services/get-item-list-fields.service';
import { QrCodeService } from '../../../services/qr-code.service';
import { SentooService } from '../../../services/sentoo.service';
import { PaymentStatus, StatusService } from '../../../services/status.service';

type AuthorizationMethod = 'qr' | 'token';

@Component({
  selector: 'ab-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit, OnDestroy {
  @Input() rendering: ComponentRendering;
  @Output() setMerchantInfo = new EventEmitter();
  @Output() setPaymentStatus = new EventEmitter<PaymentStatus>();
  @Output() setReturnUrl: EventEmitter<string> = new EventEmitter();

  public authorizationMethod: AuthorizationMethod = 'qr';
  public qrCodeImage: string;
  public apiError = false;
  public userBlockedError = false;
  public invalidCredentialsError = false;
  public helpMethod = 'token';
  public arubaAppImg = 'assets/images/aruba-app.svg';
  public desktopImg = 'assets/images/aruba-desktop.svg';
  public mobileImg = 'assets/images/icon-mobile.svg';
  public refreshImg = 'assets/images/refresh.svg';
  public retryQR = false;
  public isLoading = false;
  public formFields: FormFields;
  public items: any;

  public loginForm = new UntypedFormGroup({
    username: new UntypedFormControl('', Validators.required),
    logincode: new UntypedFormControl('', Validators.required),
    tokenType: new UntypedFormControl('hardToken'),
  });

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private statusSubscription: Subscription;
  private requestQRError: boolean;

  constructor(
    private qrCodeService: QrCodeService,
    private sentooService: SentooService,
    private loginService: LoginService,
    private router: Router,
    private statusService: StatusService,
    private getItemListFieldsService: GetItemListFieldsService
  ) {}

  get username() {
    return this.loginForm.get('username');
  }

  get logincode() {
    return this.loginForm.get('logincode');
  }

  get tokenType() {
    return this.loginForm.get('tokenType');
  }

  ngOnInit(): void {
    this.items = this.getItemListFieldsService.getItems(this.rendering?.fields?.items as Item[]);
    this.getQrCodeImage();
  }

  getQrCodeImage() {
    this.isLoading = true;
    combineLatest([this.sentooService.getCountry(), this.sentooService.getSentooTransaction()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([country, transaction]) => {
        this.qrCodeService
          .getQrCode(country, transaction)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (qrcodeImageResponse) => {
              if (this.retryQR) {
                this.retryQR = false;
              }
              this.isLoading = false;
              this.qrCodeImage = 'data:image/jpeg;base64,' + qrcodeImageResponse.qrImage;
              this.pollPaymentStatus();
            },
            () => {
              this.isLoading = false;
              this.requestQRError = true;
              this.setPaymentStatus.emit('invalid');
            }
          );
      });
  }

  pollPaymentStatus(): void {
    const timer$ = new Subject<boolean>();

    this.subscriptions.push(
      combineLatest([this.sentooService.getCountry(), this.sentooService.getSentooTransaction()]).subscribe(
        ([country, transaction]) => {
          const statusObservable$ = interval(5000).pipe(
            takeWhile(() => !this.requestQRError),
            takeUntil(timer$),
            switchMap(() => this.statusService.getStatus(country, transaction))
          );

          setTimeout(() => {
            timer$.next((this.retryQR = true));
          }, 240000);

          this.statusSubscription = statusObservable$.subscribe({
            next: (statusResponse) => {
              this.setMerchantInfo.emit(statusResponse);
              if (statusResponse && statusResponse.status === 'cancelled') {
                this.setPaymentStatus.emit('cancelled');
                this.setReturnUrl.emit(statusResponse.returnUrl);
                this.statusSubscription?.unsubscribe();
              } else if (
                statusResponse &&
                statusResponse.status === 'approved' &&
                statusResponse.numberOfApprovalsRequired > 0
              ) {
                this.setPaymentStatus.emit('pending');
                this.statusSubscription?.unsubscribe();
              } else if (statusResponse && statusResponse.status === 'approved') {
                this.setPaymentStatus.emit('complete');
                this.statusSubscription?.unsubscribe();
              }
            },
            error: (error) => {
              this.setPaymentStatus.emit('invalid');
              this.statusSubscription?.unsubscribe();
            },
          });
        }
      )
    );
  }

  login(): void {
    if (this.loginForm.valid) {
      this.loginService
        .login(this.loginForm.value as LoginRequest)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            combineLatest([this.sentooService.getCountry(), this.sentooService.getSentooTransaction()])
              .pipe(takeUntil(this.destroy$))
              .subscribe(([country, sentooTransaction]) => {
                this.router.navigate([AB_SENTOO_TRANSFER], {
                  queryParams: {
                    country,
                    'sentoo-transaction': sentooTransaction,
                  },
                });
                this.statusSubscription?.unsubscribe();
              });
          },
          (error) => {
            if (error) {
              this.apiError = true;
              this.loginForm.reset();
              this.userBlockedError = error.error.errorCode === 'User.Blocked';
              this.invalidCredentialsError = error.error.errorCode === 'User.Failed';
            }
          }
        );
    }
  }

  redirectToPaymentScreen() {
    this.subscriptions.push(
      this.router.events.pipe(filter((event: RouterEvent) => event instanceof NavigationStart)).subscribe(() => {
        this.router.navigate([AB_SENTOO_TRANSFER]);
      })
    );
  }

  selectActiveTab(method) {
    this.helpMethod = method;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
  }
}
